// AdminEventCreatePage — unified "+ New Event" full-screen creation flow.
//
// Jun 2026 (E2): replaces the four separate create buttons on the Admin >
// Events hub with ONE entry point. Admin picks an event type, then the
// matching creation form appears inline on the same page (never a modal).
//
// Reuse: the Tournament / Round Robin / Court Movement forms are the exact
// same components used before, rendered in `embedded` mode. Social Event
// keeps its dedicated rich editor. Ladder creation is new (admins asked for
// the ability to spin up brand-new ladders).

import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Users, Confetti, Ladder as LadderIcon, ArrowsLeftRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import CreateTournamentDialog from '../components/tournaments/CreateTournamentDialog';
import CreateRoundRobinDialog from '../components/roundrobin/CreateRoundRobinDialog';
import CreateSessionDialog from '../components/events/CreateSessionDialog';
import AdminClubEventEditorPage from './AdminClubEventEditorPage';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';

const TYPES = [
  { value: 'tournament',  label: 'Tournament',     icon: Trophy,          hint: 'Single-elimination bracket event.' },
  { value: 'round_robin', label: 'Round Robin',    icon: Users,           hint: 'Pool play with an optional final.' },
  { value: 'session',     label: 'Court Movement', icon: ArrowsLeftRight, hint: "Men's Night / Ladies Night court-movement league." },
  { value: 'ladder',      label: 'Ladder',         icon: LadderIcon,      hint: 'An always-open challenge ladder.' },
  { value: 'club_event',  label: 'Social Event',   icon: Confetti,        hint: 'Casual club get-together — no bracket or scoring.' },
];

export default function AdminEventCreatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState('');

  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  const goBack = () => navigate('/admin/events');

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-6" data-testid="admin-event-create-page">
      <div>
        <Link
          to="/admin/events"
          data-testid="event-create-back"
          className="inline-flex items-center gap-1.5 text-sm text-[#6a766e] hover:text-[#013523] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Events
        </Link>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight mt-2">Create New Event</h1>
        <p className="text-sm text-[#6a766e] mt-1">Pick the kind of event, then fill in the details below.</p>
      </div>

      {/* Type chooser */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">Event type</div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="event-create-type" className="w-full bg-white border-[#e3dcd1] min-h-[48px] text-[15px]">
            <SelectValue placeholder="Choose an event type…" />
          </SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`event-create-type-${t.value}`}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {type && (
          <p className="text-xs text-[#9aa39d] mt-1.5" data-testid="event-create-type-hint">
            {TYPES.find((t) => t.value === type)?.hint}
          </p>
        )}
      </div>

      {/* Form area */}
      {!type && (
        <div
          className="border border-dashed border-[#e3dcd1] rounded-2xl p-10 text-center bg-[#fbfaf8]"
          data-testid="event-create-placeholder"
        >
          <div className="text-sm text-[#6a766e]">Select an event type above to get started.</div>
        </div>
      )}

      {type === 'tournament' && (
        <CreateTournamentDialog
          embedded
          onClose={goBack}
          onCreated={(t) => navigate(`/tournaments/${t.id}/manage`)}
        />
      )}

      {type === 'round_robin' && (
        <CreateRoundRobinDialog
          embedded
          onClose={goBack}
          onCreated={(r) => navigate(`/round-robins/${r.id}/manage`)}
        />
      )}

      {type === 'session' && (
        <CreateSessionDialog
          embedded
          onClose={goBack}
          onCreated={(s) => navigate(`/admin/events/${s.id}`)}
        />
      )}

      {type === 'ladder' && (
        <LadderCreateForm
          onCancel={goBack}
          onCreated={(l) => navigate(`/admin/ladders/${l.slug}/manage`)}
        />
      )}

      {type === 'club_event' && (
        <div data-testid="event-create-club-event">
          <AdminClubEventEditorPage embedded />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ladder creation — new in E2. Ladders start active and empty; gender follows
// the existing convention (open / men's / ladies).
// ---------------------------------------------------------------------------
const GENDERS = [
  { value: 'open',   label: 'Open' },
  { value: 'mens',   label: "Men's" },
  { value: 'womens', label: 'Ladies' },
];

function LadderCreateForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('open');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    if (!name.trim()) return toast.error('Ladder name is required.');
    setBusy(true);
    try {
      const payload = { name: name.trim(), gender: gender === 'open' ? null : gender };
      const created = (await adminAPI.createLadder(payload)).data;
      toast.success('Ladder created.');
      onCreated?.(created);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not create ladder.'));
    } finally {
      setBusy(false);
    }
  };

  const label = 'text-[11px] uppercase tracking-wider text-[#6a766e]';
  const input = 'mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm min-h-[44px] focus:outline-none focus:border-[#015e3f] focus:ring-2 focus:ring-[#015e3f]/20';

  return (
    <div className="bg-white border border-[#e3dcd1] rounded-2xl overflow-hidden" data-testid="create-ladder-embedded">
      <div className="px-5 pt-5 sm:px-6 sm:pt-6">
        <h2 className="text-lg font-semibold text-[#013523]">New ladder</h2>
        <p className="text-sm text-[#6a766e]">A fresh, always-open challenge ladder. Add players from Manage once it's created.</p>
      </div>
      <div className="px-5 py-4 sm:px-6 space-y-4">
        <label className="block">
          <span className={label}>Ladder name</span>
          <input
            data-testid="create-ladder-name"
            autoFocus type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Summer Singles Ladder"
            className={input}
            maxLength={120}
          />
        </label>
        <div>
          <div className={label}>Gender</div>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {GENDERS.map((g) => (
              <button
                key={g.value}
                type="button"
                data-testid={`create-ladder-gender-${g.value}`}
                onClick={() => setGender(g.value)}
                className={`py-2 rounded-lg border text-sm min-h-[44px] ${gender === g.value ? 'bg-[#013523] text-white border-[#013523]' : 'border-[#e3dcd1] text-[#013523]'}`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#9aa39d] mt-1">
            Gendered ladders only accept members whose profile matches.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 px-5 pb-5 sm:px-6">
        <button
          type="button"
          data-testid="create-ladder-cancel"
          onClick={onCancel}
          disabled={busy}
          className="px-4 py-2 rounded-lg border border-[#e3dcd1] text-[#013523] text-sm disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="create-ladder-submit"
          onClick={submit}
          disabled={busy || !name.trim()}
          className="px-4 py-2 rounded-lg bg-[#013523] text-white text-sm font-medium disabled:opacity-50 hover:bg-[#024029] transition-colors"
        >
          {busy ? 'Creating…' : 'Create ladder'}
        </button>
      </div>
    </div>
  );
}
